// CO - Vistos recentemente — comportamento de sections/conso-vistos-recentemente.liquid.
//
// Histórico no localStorage (`conso_vistos`): array de handles, mais recente
// primeiro, sem repetição, até 20. A PDP grava o produto atual ao carregar.
//
// Os cards NÃO são montados aqui: cada um vem renderizado pelo tema via Section
// Rendering API (`/products/<handle>?view=<template>&section_id=<section>`), que
// devolve esta mesma section no contexto do produto buscado — o card fica no
// <template data-conso-vistos-card>. Busca só quando a section se aproxima da tela.

if (!customElements.get('conso-vistos-recentemente')) {
  const CHAVE = 'conso_vistos';
  const LIMITE_HISTORICO = 20;

  const lerHistorico = () => {
    try {
      const lista = JSON.parse(localStorage.getItem(CHAVE) || '[]');
      return Array.isArray(lista) ? lista.filter((h) => typeof h === 'string' && h) : [];
    } catch {
      return [];
    }
  };

  const gravarHistorico = (lista) => {
    try {
      localStorage.setItem(CHAVE, JSON.stringify(lista.slice(0, LIMITE_HISTORICO)));
    } catch {
      /* modo privado / storage bloqueado: segue sem histórico */
    }
  };

  class ConsoVistosRecentemente extends HTMLElement {
    #carregado = false;
    #observer = null;

    connectedCallback() {
      const atual = this.dataset.handle;
      // Lê ANTES de gravar: o produto atual não entra na própria lista.
      this.handles = lerHistorico().filter((h) => h !== atual);
      if (atual) gravarHistorico([atual, ...this.handles]);

      if (this.handles.length === 0) return;

      this.#observer = new IntersectionObserver(
        (entradas) => {
          if (!entradas.some((e) => e.isIntersecting)) return;
          this.#observer.disconnect();
          this.#carregar();
        },
        { rootMargin: '600px 0px' }
      );
      // Oculto, o elemento não tem caixa, e o wrapper .shopify-section fica com
      // altura 0 — alvo de área zero dentro do scroll do .page-wrapper não dispara o
      // observer. Observa a section anterior (o "Você também pode gostar"), que tem
      // altura; sem ela, carrega quando o navegador estiver ocioso.
      const anterior = this.closest('.shopify-section')?.previousElementSibling;
      if (anterior) {
        this.#observer.observe(anterior);
      } else {
        this.#observer.disconnect();
        (window.requestIdleCallback || setTimeout)(() => this.#carregar());
      }
    }

    disconnectedCallback() {
      this.#observer?.disconnect();
    }

    #url(handle) {
      const raiz = (this.dataset.root || '/').replace(/\/$/, '');
      const params = new URLSearchParams({ section_id: this.dataset.sectionId });
      if (this.dataset.view) params.set('view', this.dataset.view);
      return `${raiz}/products/${encodeURIComponent(handle)}?${params}`;
    }

    async #buscarCard(handle) {
      try {
        const resposta = await fetch(this.#url(handle), { headers: { Accept: 'text/html' } });
        if (!resposta.ok) return { handle, card: null, remover: resposta.status === 404 };
        const html = new DOMParser().parseFromString(await resposta.text(), 'text/html');
        const modelo = html.querySelector('template[data-conso-vistos-card]');
        const card = modelo?.content.firstElementChild;
        // Produto apagado/despublicado: a Section Rendering responde 200, mas sem
        // `product` a section não imprime o <template>. Não entra, e sai do histórico.
        return { handle, card: card ? document.importNode(card, true) : null, remover: !modelo };
      } catch {
        return { handle, card: null, remover: false };
      }
    }

    async #carregar() {
      if (this.#carregado) return;
      this.#carregado = true;

      const max = Number(this.dataset.max) || 8;
      // Pede um pouco além do máximo pra cobrir produtos que sumiram da loja.
      const alvo = this.handles.slice(0, max + 4);
      const resultados = await Promise.all(alvo.map((h) => this.#buscarCard(h)));

      const removidos = resultados.filter((r) => r.remover).map((r) => r.handle);
      if (removidos.length) gravarHistorico(lerHistorico().filter((h) => !removidos.includes(h)));

      const cards = resultados.filter((r) => r.card).slice(0, max).map((r) => r.card);
      if (cards.length === 0) return;

      const trilha = this.querySelector('[data-conso-vistos-trilha]');
      trilha.replaceChildren(...cards);
      this.hidden = false;
      this.#ligarSetas(trilha);
    }

    #ligarSetas(trilha) {
      const setas = Array.from(this.querySelectorAll('[data-conso-vistos-seta]'));
      if (setas.length === 0) return;

      const atualizar = () => {
        const max = trilha.scrollWidth - trilha.clientWidth;
        for (const seta of setas) {
          const dir = Number(seta.dataset.consoVistosSeta);
          seta.hidden = max <= 1 || (dir < 0 ? trilha.scrollLeft <= 1 : trilha.scrollLeft >= max - 1);
        }
      };

      for (const seta of setas) {
        seta.addEventListener('click', () => {
          const item = trilha.firstElementChild;
          const passo = item ? item.getBoundingClientRect().width + parseFloat(getComputedStyle(trilha).columnGap || '0') : trilha.clientWidth;
          // scrollLeft/scrollBy direto na trilha: scrollIntoView arrastaria a página junto.
          trilha.scrollBy({ left: Number(seta.dataset.consoVistosSeta) * passo, behavior: 'smooth' });
        });
      }

      trilha.addEventListener('scroll', atualizar, { passive: true });
      new ResizeObserver(atualizar).observe(trilha);
      atualizar();
    }
  }

  customElements.define('conso-vistos-recentemente', ConsoVistosRecentemente);
}
